import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "src/common/axiosInstance";

import LeadTabs from "./components/LeadDetails/LeadTabs";
import LeadInfoCards from "./components/LeadDetails/LeadInfoCards";
import LeadStagePanel from "./components/LeadStagePanel";
import LeadOwnersPanel from "./components/LeadOwnersPanel/LeadOwnersPanel";
import ActivityStrip from "./components/LeadDetails/ActivityStrip";
import AdditionalDetailsCard from "./components/LeadAdditionalDetails/AdditionalDetailsCard";
import LeadDocumentsPanel from "./Documents/LeadDocumentsPanel";
import LeadDuplicates from "./components/LeadDetails/LeadDuplicates";

export default function LeadDetails({ leadId, workspaceId, onLeadFetched, refreshKey, onOpenLead }) {
  const params = useParams();
  const id = leadId || params.id;

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestVersion = useRef(0);
  const [activitiesRefreshKey, setActivitiesRefreshKey] = useState(0);
  const onLeadFetchedRef = useRef(onLeadFetched);

  useEffect(() => {
    onLeadFetchedRef.current = onLeadFetched;
  }, [onLeadFetched]);

  const fetchLead = useCallback(async () => {
    const version = ++requestVersion.current;
    setError("");
    try {
      const res = await axiosInstance.get(`/leads/${id}`, {
        headers: workspaceId ? { "X-Workspace-ID": workspaceId } : undefined,
      });
      if (version !== requestVersion.current) return;
      const leadData = res.data?.data || res.data;
      setLead(leadData);
      onLeadFetchedRef.current?.(leadData);
    } catch (error) {
      if (version === requestVersion.current) setError(error?.response?.data?.message || "Unable to load this lead.");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [id, workspaceId]);

  useEffect(() => {
    setLoading(true);
    setLead(null);
    fetchLead();
    return () => { requestVersion.current += 1; };
  }, [fetchLead, refreshKey]);

  const handleDetailsSaved = (updatedLead) => {
    if (String(updatedLead?.id) !== String(id)) return;
    requestVersion.current += 1;
    setLead(updatedLead);
    onLeadFetchedRef.current?.(updatedLead);
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  if (!lead) {
    return <div className="space-y-3 p-8"><p role="alert">{error || "Lead not found"}</p><button type="button" onClick={fetchLead} className="font-semibold text-[#147db5]">Try again</button></div>;
  }

  return (
    <div className="space-y-5">
      <LeadDuplicates lead={lead} onOpenLead={onOpenLead} />
      
      <div id="quick-actions">
        <ActivityStrip
          leadId={id}
          phone={lead.phone}
          onSuccess={() => setActivitiesRefreshKey((prev) => prev + 1)}
        />
      </div>

      <div id="lead-workflow">
        <LeadStagePanel
          leadId={id}
          lead={lead}
          onWorkflowChanged={fetchLead}
        />
      </div>

      <LeadOwnersPanel leadId={id} workspaceId={workspaceId}/>

      <div id="lead-info">
        <LeadInfoCards lead={lead} />
      </div>

      <div id="lead-additional-details">
        <AdditionalDetailsCard lead={lead} workspaceId={workspaceId} onSaved={handleDetailsSaved} />
      </div>

      <div id="lead-details" className="mt-6">
        <LeadTabs
          workspaceId={workspaceId}
          leadId={id}
          activitiesRefreshKey={activitiesRefreshKey}
        />
      </div>
      <LeadDocumentsPanel leadId={id} leadName={lead.name} workspaceId={workspaceId} />
    </div>
  );
}
